import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, BookOpen, Sparkles, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TOPIC_ORDER = ['generative-ai', 'agentic-ai', 'rag', 'llms', 'machine-learning'];

const TOPIC_COLORS = {
  'generative-ai': 'bg-blue-50 text-blue-700 border-blue-100',
  'agentic-ai': 'bg-emerald-50 text-emerald-700 border-emerald-100',
  'rag': 'bg-amber-50 text-amber-700 border-amber-100',
  'llms': 'bg-violet-50 text-violet-700 border-violet-100',
  'machine-learning': 'bg-rose-50 text-rose-700 border-rose-100',
};

const Learn = () => {
  const [lessons, setLessons] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const subEmail = localStorage.getItem('subscriber_email') || '';
        const headers = subEmail ? { 'X-Subscriber-Email': subEmail } : {};
        const [tRes, lRes] = await Promise.all([
          axios.get(`${API_URL}/api/lessons/topics`),
          axios.get(`${API_URL}/api/lessons`, { headers }),
        ]);
        const sortedTopics = tRes.data.sort(
          (a, b) => TOPIC_ORDER.indexOf(a.slug) - TOPIC_ORDER.indexOf(b.slug)
        );
        setTopics(sortedTopics);
        setLessons(lRes.data);
      } catch (e) {
        console.error(e);
        setError('Failed to load lessons');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = selectedTopic === 'all'
    ? lessons
    : lessons.filter(l => l.topicSlug === selectedTopic);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="learn-loading">
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="learn-page">
      {/* Hero */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-700 mb-6">
            <Sparkles size={16} />
            <span>Simple lessons for curious minds</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-light mb-6 tracking-tight leading-tight">
            Learn
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Bite-sized lessons on Generative AI, Agentic AI, RAG, LLMs, and Machine Learning —
            written for high schoolers in plain English.
          </p>
        </div>
      </section>

      {/* Topic pills */}
      <section className="px-6 mb-12">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-3 justify-center">
          <button
            onClick={() => setSelectedTopic('all')}
            className={`px-5 py-2 rounded-full text-sm border transition-all hover:-translate-y-0.5 ${
              selectedTopic === 'all'
                ? 'bg-black text-white border-black'
                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
            }`}
            data-testid="topic-filter-all"
          >
            All Lessons
          </button>
          {topics.map(t => (
            <button
              key={t.slug}
              onClick={() => setSelectedTopic(t.slug)}
              className={`px-5 py-2 rounded-full text-sm border transition-all hover:-translate-y-0.5 ${
                selectedTopic === t.slug
                  ? 'bg-black text-white border-black'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
              }`}
              data-testid={`topic-filter-${t.slug}`}
            >
              {t.name}
              <span className="ml-2 text-xs opacity-70">{t.count}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Lessons grid */}
      <section className="px-6 pb-24">
        <div className="max-w-7xl mx-auto">
          {error ? (
            <p className="text-center text-red-600">{error}</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-light mb-2">No lessons here yet</h3>
              <p className="text-gray-500">
                Check back soon — new lessons are added regularly.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="lessons-grid">
              {filtered.map(lesson => (
                <Link
                  key={lesson.id}
                  to={`/learn/${lesson.slug}`}
                  className="group block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md hover:-translate-y-1 transition-all"
                  data-testid={`lesson-card-${lesson.slug}`}
                >
                  <div className={`inline-block text-xs px-3 py-1 rounded-full border mb-4 ${TOPIC_COLORS[lesson.topicSlug] || 'bg-gray-50 text-gray-700 border-gray-100'}`}>
                    {lesson.topic}
                  </div>
                  <h3 className="text-xl font-medium mb-3 leading-snug group-hover:text-black">
                    {lesson.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                    {lesson.summary}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{lesson.readTime}</span>
                    <span className="flex items-center gap-1 text-gray-700 group-hover:translate-x-1 transition-transform">
                      Read <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Learn;
